/**
 * Predefined color palette for categorical data
 * Designed to be visually distinct and colorblind-friendly
 */
const CATEGORY_COLORS = [
  '#EA4335', // Red
  '#4285F4', // Blue
  '#34A853', // Green
  '#FBBC04', // Yellow
  '#FF6D00', // Orange
  '#9C27B0', // Purple
  '#00BCD4', // Cyan
  '#E91E63', // Pink
  '#009688', // Teal
  '#FF5722', // Deep Orange
  '#3F51B5', // Indigo
  '#CDDC39', // Lime
  '#795548', // Brown
  '#607D8B', // Blue Grey
  '#FFC107', // Amber
  '#8BC34A', // Light Green
  '#673AB7', // Deep Purple
  '#00897B', // Teal 700
  '#D32F2F', // Red 700
  '#1976D2', // Blue 700
];

/**
 * Map of unique values to assigned colors
 * This ensures consistent colors across renders
 */
const colorAssignments = new Map();
let nextColorIndex = 0;

/**
 * Get color for a categorical value
 * Assigns consistent colors to unique values
 */
export function getCategoryColor(value) {
  if (!value) return '#9E9E9E'; // Grey for null/empty

  const key = String(value).trim();

  if (colorAssignments.has(key)) {
    return colorAssignments.get(key);
  }

  // Assign next color from palette
  const color = CATEGORY_COLORS[nextColorIndex % CATEGORY_COLORS.length];
  colorAssignments.set(key, color);
  nextColorIndex++;

  return color;
}

/**
 * Reset color assignments (call when loading new data)
 */
export function resetColorAssignments() {
  colorAssignments.clear();
  nextColorIndex = 0;
}

/**
 * Get all current color assignments
 */
export function getColorAssignments() {
  return new Map(colorAssignments);
}

/**
 * Get color assignments as a plain object (for saving to config)
 */
export function getColorAssignmentsObject() {
  const obj = {};
  colorAssignments.forEach((color, key) => {
    obj[key] = color;
  });
  return obj;
}

/**
 * Restore color assignments from a saved object
 */
export function restoreColorAssignments(savedAssignments) {
  if (!savedAssignments || typeof savedAssignments !== 'object') {
    return;
  }

  colorAssignments.clear();
  Object.entries(savedAssignments).forEach(([key, color]) => {
    colorAssignments.set(key, color);
  });
  nextColorIndex = colorAssignments.size;
}

/**
 * Set a custom color for a category value
 */
export function setCategoryColor(value, color) {
  const key = String(value).trim();
  colorAssignments.set(key, color);
}

/**
 * Convert color name or hex to valid hex color
 */
export function getMarkerColor(color) {
  if (!color) return '#4285F4'; // Default Google Maps blue

  const colorStr = String(color).trim();

  // If already hex, validate and return
  if (colorStr.startsWith('#')) {
    return /^#[0-9A-F]{6}$/i.test(colorStr) ? colorStr : '#4285F4';
  }

  // Common color names to hex (more comprehensive list)
  const colorMap = {
    // Basic colors
    red: '#EA4335',
    blue: '#4285F4',
    green: '#34A853',
    yellow: '#FBBC04',
    orange: '#FF6D00',
    purple: '#9C27B0',
    pink: '#E91E63',
    brown: '#795548',
    gray: '#9E9E9E',
    grey: '#9E9E9E',
    black: '#000000',
    white: '#FFFFFF',

    // Extended colors
    cyan: '#00BCD4',
    magenta: '#E91E63',
    lime: '#CDDC39',
    navy: '#000080',
    teal: '#009688',
    olive: '#808000',
    maroon: '#800000',
    indigo: '#3F51B5',

    // Status-based colors
    active: '#34A853',
    inactive: '#9E9E9E',
    pending: '#FBBC04',
    complete: '#34A853',
    completed: '#34A853',
    cancelled: '#EA4335',
    'in progress': '#4285F4',
    'in-progress': '#4285F4',
    new: '#9C27B0',
    urgent: '#EA4335',
    high: '#FF6D00',
    medium: '#FBBC04',
    low: '#34A853',
  };

  const lowerColor = colorStr.toLowerCase();

  // Check for exact match first
  if (colorMap[lowerColor]) {
    return colorMap[lowerColor];
  }

  // Check for partial matches (e.g., "Active Site" contains "active")
  for (const [key, value] of Object.entries(colorMap)) {
    if (lowerColor.includes(key)) {
      return value;
    }
  }

  // Generate consistent color from string hash
  return stringToColor(colorStr);
}

/**
 * Generate a consistent color from a string using hash
 */
function stringToColor(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }

  // Generate vibrant, distinguishable colors
  const hue = Math.abs(hash % 360);
  const saturation = 65 + (Math.abs(hash % 20)); // 65-85%
  const lightness = 45 + (Math.abs((hash >> 8) % 15)); // 45-60%

  return hslToHex(hue, saturation, lightness);
}

/**
 * Convert HSL to HEX
 */
function hslToHex(h, s, l) {
  s /= 100;
  l /= 100;

  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs((h / 60) % 2 - 1));
  const m = l - c / 2;

  let r = 0, g = 0, b = 0;

  if (h < 60) {
    r = c; g = x; b = 0;
  } else if (h < 120) {
    r = x; g = c; b = 0;
  } else if (h < 180) {
    r = 0; g = c; b = x;
  } else if (h < 240) {
    r = 0; g = x; b = c;
  } else if (h < 300) {
    r = x; g = 0; b = c;
  } else {
    r = c; g = 0; b = x;
  }

  const toHex = (n) => {
    const hex = Math.round((n + m) * 255).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/**
 * Check if a point is inside a polygon
 */
export function isPointInPolygon(point, polygon) {
  const x = point.lat;
  const y = point.lng;

  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].lat;
    const yi = polygon[i].lng;
    const xj = polygon[j].lat;
    const yj = polygon[j].lng;

    const intersect = ((yi > y) !== (yj > y))
      && (x < (xj - xi) * (y - yi) / (yj - yi) + xi);

    if (intersect) inside = !inside;
  }

  return inside;
}

/**
 * Check if a point is inside a rectangle bounds
 */
export function isPointInRectangle(point, bounds) {
  const { north, south, east, west } = bounds;
  return point.lat >= south && point.lat <= north
    && point.lng >= west && point.lng <= east;
}

/**
 * Store for hidden groups (group visibility state)
 */
let hiddenGroupsState = new Set();

/**
 * Get hidden groups as an array (for saving to config)
 */
export function getHiddenGroupsArray() {
  return Array.from(hiddenGroupsState);
}

/**
 * Restore hidden groups from a saved array
 */
export function restoreHiddenGroups(savedHiddenGroups) {
  if (!Array.isArray(savedHiddenGroups)) {
    hiddenGroupsState = new Set();
    return;
  }
  hiddenGroupsState = new Set(savedHiddenGroups);
}

/**
 * Update hidden groups state
 */
export function setHiddenGroups(hiddenGroups) {
  if (hiddenGroups instanceof Set) {
    hiddenGroupsState = new Set(hiddenGroups);
  } else if (Array.isArray(hiddenGroups)) {
    hiddenGroupsState = new Set(hiddenGroups);
  }
}

/**
 * Get current hidden groups
 */
export function getHiddenGroups() {
  return new Set(hiddenGroupsState);
}
